/*
 * @Copyright 2026. Institute for Future Intelligence, Inc.
 */

import { Vector3 } from 'three';
import { Atom } from '../models/Atom.ts';
import Element from '../lib/chem/Element';
import { PeriodicBox } from '../models/crystal/PeriodicBox.ts';
import { MolecularViewerMaterial, MolecularViewerStyle } from '../view/displayOptions.ts';

// CPK / Jmol element colors. Subset covering the elements that appear in our
// preset crystals plus their common neighbors — anything unrecognized falls
// back to a neutral gray. Lifted out of the viewer so both the gallery
// thumbnail and the chamber renderer agree on the same palette.
//
// Values mirror src/lib/gfx/palettes/jmolPalette.js so the chamber and the
// Miew-backed gallery thumbnails read consistently. When adding a new
// preset crystal that introduces an element not already listed, add it here
// too — otherwise both atom species in a binary (e.g. Ga/As, Ga/P, In/Sb)
// fall to the same neutral fallback and the structure renders monochrome.
export const ELEMENT_COLORS: Record<string, string> = {
  H: '#ffffff',
  He: '#d9ffff',
  Li: '#cc80ff',
  Be: '#c2ff00',
  B: '#ffb5b5',
  C: '#909090',
  N: '#3050f8',
  O: '#ff0d0d',
  F: '#90e050',
  Ne: '#b3e3f5',
  Na: '#ab5cf2',
  Mg: '#8aff00',
  Al: '#bfa6a6',
  Si: '#f0c8a0',
  P: '#ff8000',
  S: '#ffff30',
  Cl: '#1ff01f',
  Ar: '#80d1e3',
  K: '#8f40d4',
  Ca: '#3dff00',
  Ti: '#bfc2c7',
  V: '#a6a6ab',
  Cr: '#8a99c7',
  Mn: '#9c7ac7',
  Fe: '#e06633',
  Co: '#f090a0',
  Ni: '#50d050',
  Cu: '#c88033',
  Zn: '#7d80b0',
  Ga: '#c28f8f',
  Ge: '#668f8f',
  As: '#bd80e3',
  Se: '#ffa100',
  Br: '#a62929',
  Kr: '#5cb8d1',
  Rb: '#702eb0',
  Sr: '#00ff00',
  Y: '#94ffff',
  Zr: '#94e0e0',
  Mo: '#54b5b5',
  Ru: '#248f8f',
  Rh: '#0a7d8c',
  Pd: '#006985',
  Ag: '#c0c0c0',
  Cd: '#ffd98f',
  In: '#a67573',
  Sn: '#668080',
  Sb: '#9e63b5',
  Te: '#d47a00',
  I: '#940094',
  Xe: '#429eb0',
  Cs: '#57178f',
  Ba: '#00c900',
  Ta: '#4da6ff',
  W: '#2194d6',
  Pt: '#d0d0e0',
  Au: '#ffd123',
  Hg: '#b8b8d0',
  Pb: '#575961',
  Bi: '#9e4fb5',
};

export const elementColor = (symbol: string): string => ELEMENT_COLORS[symbol] ?? '#bdbdbd';

// 0.35× Miew's stored VdW-ish radius matches the chamber's ball-and-stick
// proportions closely enough that crystal atoms read at the same visual
// weight as molecular atoms.
// Element.ByName is keyed by uppercase element names ('NA', 'CL', ...) per
// Element.js. Calling getByName with mixed case ('Na') silently auto-creates
// a placeholder Element with radius=1, radiusBonding=0.01 — which destroys
// the bond-distance cutoff. Always uppercase before lookup.
const lookupElement = (symbol: string): { radius?: number; radiusBonding?: number } | null => {
  try {
    return Element.getByName(symbol.toUpperCase());
  } catch {
    return null;
  }
};

export const atomRadius = (symbol: string, scale: number = 0.35): number => {
  const r = lookupElement(symbol)?.radius;
  return Number.isFinite(r) && (r as number) > 0 ? (r as number) * scale : 0.4;
};

// Covalent / bonding radius (Å) used for the bond-detection distance cutoff.
// Falls back to 0.75 Å if the element is unknown — the same approximate
// scale as carbon's covalent radius.
//
// A few elements in the chem library have `radiusBonding` values that are
// notably below their standard single-bond covalent radii — small enough
// that the ×1.15 cutoff fails to capture real bonds. The overrides below
// are element-specific (rather than a global floor) so we only inflate
// the radii that actually need it, leaving the library's tuned values
// elsewhere alone. Sources are Pauling / Cordero standard values:
//   H — library 0.23, standard ~0.31 (CH/NH/OH/HH bonds need ≥0.32)
//   P — library 0.75, standard ~1.10 (P-P in P₄ is 2.21 Å, needs ≥0.97)
const RADIUS_OVERRIDES: Record<string, number> = {
  H: 0.37,
  // Deuterium and tritium are hydrogen: same bonding radius. Without these
  // they fell back to the library's 0.23 Å and no C–D / O–D bond was drawn.
  D: 0.37,
  T: 0.37,
  P: 1.1,
};
const bondingRadius = (symbol: string): number => {
  const override = RADIUS_OVERRIDES[symbol];
  if (override !== undefined) return override;
  const r = lookupElement(symbol)?.radiusBonding;
  if (!Number.isFinite(r) || (r as number) <= 0) return 0.75;
  return r as number;
};

export interface BondPair {
  a: number; // atom index
  b: number; // atom index
}

// Distance-cutoff bond detection. The right cutoff is context-dependent and
// there's no single rule that satisfies every crystal type:
//
//   Ionic   — bond length > covalent-radius sum (Na-Cl: cov-sum 1.96, dist
//             2.82). Needs a generous additive tolerance.
//   Metallic / covalent — 2nd-neighbor distances are close to 1st (Cu FCC:
//             2.56 vs 3.62; diamond C-C: 1.54 vs 2.51). Needs a tight
//             multiplicative cutoff to avoid over-bonding 2nd neighbors.
//
// When the caller provides `charges`, we treat the structure as ionic:
// only opposite-sign pairs bond, and the cutoff is covalent-sum + 1.0 Å.
// Same-sign pairs never bond (cation-cation, anion-anion). Without charges,
// we use (r1+r2) × 1.15 — tight enough to exclude 2nd-neighbor pairs in
// Cu/Si/Diamond/graphite while still catching 1st neighbors.
//
// Fe BCC is a known limitation: 1st (2.48 Å) and 2nd (2.87 Å) neighbors
// have a ratio of only 1.16, so any cutoff loose enough to bond diamond
// (which needs ≥1.13×) also bonds Fe's 2nd. Acceptable v1 trade-off.
//
// O(N²) pairwise; fine for supercell sizes under ~1k atoms (our 5×5×5 cap).
const IONIC_TOLERANCE = 1.0;
const NON_IONIC_SCALE = 1.15;

/**
 * Render-only ghost atoms — periodic-image copies of supercell atoms placed
 * just outside the supercell so bonds at the cell boundary draw as short
 * stubs into the adjacent image instead of either being missing entirely
 * (orphan boundary atoms) or being drawn as a long cylinder spanning the
 * full cell.
 *
 * For each real atom, the 26 adjacent periodic images are tested; an image
 * is kept as a ghost only if SOME other real atom sits inside the
 * element-pair bond cutoff (so the ghost actually participates in a bond
 * when computeBonds runs). This element-pair check exactly mirrors the
 * cutoff math in `computeBonds`, so every ghost we generate is guaranteed
 * to be one bond endpoint — no orphan ghosts. Ghost-to-ghost bonds are
 * separately filtered by the caller via `isGhost`.
 *
 * Ghosts carry `isGhost = true` so the renderer can opt them out of picking
 * and cluster-highlight logic, and so the MD engine (which never sees them)
 * stays clean.
 *
 * O(N · 26 · N). For supercells up to ~600 atoms (FAU conventional cell)
 * this is ~9M ops — well inside a per-frame budget.
 */
export const computeGhostAtoms = (
  realAtoms: Atom[],
  periodicBox: PeriodicBox,
  startIndex: number,
  charges?: Record<string, number>,
  // COVALENT-FRAMEWORK boundary-shell mode. When set, instead of the bond-
  // driven criterion below (which only adds single bonded partners and so
  // can't complete a pore RING that arcs through a face), keep EVERY periodic
  // image that lands within `marginAngstrom` of the supercell box. That shows
  // the neighboring-cell atoms that close the boundary pores, so the rendered
  // block reads as a symmetric cut of the infinite framework. `center` maps a
  // Cartesian position back to a supercell-fractional coordinate (atoms are
  // origin-centered, so we shift by +center before hInv).
  shell?: { center: Vector3; marginAngstrom: number },
): Atom[] => {
  if (realAtoms.length === 0) return [];
  // Build the 26 neighbor-image offsets from the periodic box's lattice
  // vectors. h is column-major Matrix3: h.elements = [Ax, Ay, Az, Bx, By,
  // Bz, Cx, Cy, Cz] (three.js stores Matrix3 column-major).
  const h = periodicBox.h.elements;
  const Ax = h[0],
    Ay = h[1],
    Az = h[2];
  const Bx = h[3],
    By = h[4],
    Bz = h[5];
  const Cx = h[6],
    Cy = h[7],
    Cz = h[8];
  const offsets: Vector3[] = [];
  for (let i = -1; i <= 1; i++) {
    for (let j = -1; j <= 1; j++) {
      for (let k = -1; k <= 1; k++) {
        if (i === 0 && j === 0 && k === 0) continue;
        offsets.push(new Vector3(i * Ax + j * Bx + k * Cx, i * Ay + j * By + k * Cy, i * Az + j * Bz + k * Cz));
      }
    }
  }

  // Boundary-shell mode (covalent frameworks): keep every periodic image whose
  // supercell-fractional coordinate falls within `fm` of the [0,1] cell on each
  // axis. fm is the margin expressed as a fraction of each cell edge.
  if (shell) {
    const hInv = periodicBox.hInv;
    const axisLen = [Math.hypot(Ax, Ay, Az), Math.hypot(Bx, By, Bz), Math.hypot(Cx, Cy, Cz)];
    const fm = [
      axisLen[0] > 0 ? shell.marginAngstrom / axisLen[0] : 0,
      axisLen[1] > 0 ? shell.marginAngstrom / axisLen[1] : 0,
      axisLen[2] > 0 ? shell.marginAngstrom / axisLen[2] : 0,
    ];
    const ghosts: Atom[] = [];
    let gidx = startIndex;
    const f = new Vector3();
    for (let i = 0; i < realAtoms.length; i++) {
      const a = realAtoms[i];
      if (a.removed) continue;
      for (const off of offsets) {
        const gx = a.position.x + off.x;
        const gy = a.position.y + off.y;
        const gz = a.position.z + off.z;
        f.set(gx, gy, gz).add(shell.center).applyMatrix3(hInv);
        if (f.x < -fm[0] || f.x > 1 + fm[0]) continue;
        if (f.y < -fm[1] || f.y > 1 + fm[1]) continue;
        if (f.z < -fm[2] || f.z > 1 + fm[2]) continue;
        const ghost = new Atom(gidx++, a.elementSymbol, new Vector3(gx, gy, gz));
        ghost.isGhost = true;
        ghost.ghostSource = i;
        ghost.imageOffset = off.clone();
        ghosts.push(ghost);
      }
    }
    return ghosts;
  }

  const radii = realAtoms.map((a) => bondingRadius(a.elementSymbol));
  const isIonic = !!charges;
  const atomCharges = realAtoms.map((a) => charges?.[a.elementSymbol] ?? 0);
  // Per-pair cutoff — matches the math inside computeBonds. Returns 0 when
  // no bond is possible (same-sign ions); the squared-distance check then
  // can't pass.
  const pairCutoff = (i: number, j: number): number => {
    if (!isIonic) return (radii[i] + radii[j]) * NON_IONIC_SCALE;
    const c1 = atomCharges[i];
    const c2 = atomCharges[j];
    if (c1 * c2 < 0) return radii[i] + radii[j] + IONIC_TOLERANCE;
    if (c1 === 0 || c2 === 0) return (radii[i] + radii[j]) * NON_IONIC_SCALE;
    return 0;
  };
  // Hash the real atoms into a grid whose cell is the largest possible pair
  // cutoff, so an image only tests the atoms in its own and the 26 adjacent
  // cells. A brute-force scan over every real atom was O(N²·26) — ~6M pair
  // tests for a 500-atom supercell, the bulk of a lab's first render — while
  // most images land outside the supercell and find no neighbors at all.
  // Same accept rule as before (any partner within its pair cutoff), so the
  // ghost set is unchanged.
  let maxRadius = 0;
  for (const r of radii) if (r > maxRadius) maxRadius = r;
  const cellSize = Math.max(2 * maxRadius * NON_IONIC_SCALE, isIonic ? 2 * maxRadius + IONIC_TOLERANCE : 0, 1e-3);
  const cellKey = (cx: number, cy: number, cz: number) => ((cx + 1024) * 2048 + (cy + 1024)) * 2048 + (cz + 1024);
  const grid = new Map<number, number[]>();
  let minX = Infinity,
    minY = Infinity,
    minZ = Infinity,
    maxX = -Infinity,
    maxY = -Infinity,
    maxZ = -Infinity;
  for (let j = 0; j < realAtoms.length; j++) {
    const p = realAtoms[j].position;
    if (p.x < minX) minX = p.x;
    if (p.y < minY) minY = p.y;
    if (p.z < minZ) minZ = p.z;
    if (p.x > maxX) maxX = p.x;
    if (p.y > maxY) maxY = p.y;
    if (p.z > maxZ) maxZ = p.z;
    const k = cellKey(Math.floor(p.x / cellSize), Math.floor(p.y / cellSize), Math.floor(p.z / cellSize));
    const bin = grid.get(k);
    if (bin) bin.push(j);
    else grid.set(k, [j]);
  }
  const ghosts: Atom[] = [];
  let idx = startIndex;
  for (let i = 0; i < realAtoms.length; i++) {
    const a = realAtoms[i];
    for (const off of offsets) {
      const gx = a.position.x + off.x;
      const gy = a.position.y + off.y;
      const gz = a.position.z + off.z;
      // Keep this image only if it WOULD form a bond to some other real
      // atom under the same cutoff rule computeBonds uses.
      // Most images sit beyond bonding range of the whole supercell.
      if (
        gx < minX - cellSize ||
        gx > maxX + cellSize ||
        gy < minY - cellSize ||
        gy > maxY + cellSize ||
        gz < minZ - cellSize ||
        gz > maxZ + cellSize
      ) {
        continue;
      }
      let useful = false;
      const cx = Math.floor(gx / cellSize);
      const cy = Math.floor(gy / cellSize);
      const cz = Math.floor(gz / cellSize);
      search: for (let ox = -1; ox <= 1; ox++) {
        for (let oy = -1; oy <= 1; oy++) {
          for (let oz = -1; oz <= 1; oz++) {
            const bin = grid.get(cellKey(cx + ox, cy + oy, cz + oz));
            if (!bin) continue;
            for (const j of bin) {
              if (i === j) continue;
              const cutoff = pairCutoff(i, j);
              if (cutoff <= 0) continue;
              const p = realAtoms[j].position;
              const dx = gx - p.x;
              const dy = gy - p.y;
              const dz = gz - p.z;
              const d2 = dx * dx + dy * dy + dz * dz;
              if (d2 > 0.01 && d2 < cutoff * cutoff) {
                useful = true;
                break search;
              }
            }
          }
        }
      }
      if (useful) {
        const ghost = new Atom(idx++, a.elementSymbol, new Vector3(gx, gy, gz));
        ghost.isGhost = true;
        ghost.ghostSource = i;
        ghost.imageOffset = off.clone(); // constant lattice translation from source
        ghosts.push(ghost);
      }
    }
  }
  return ghosts;
};

/**
 * Cosmetic boundary-completion image atoms — purely visual. For an ATOMIC
 * crystal (no discrete molecular topology: metals, ionic salts, covalent
 * networks, noble-gas solids), any atom that sits on a LOWER bounding plane
 * of the supercell (its supercell-fractional coordinate is ≈ 0 along an
 * axis) is mirrored onto the opposite (upper) plane — plus the edge and
 * corner combinations when it lies on two or three lower planes at once.
 * This closes the rendered nx·ny·nz block into the symmetric
 * conventional-cell shape textbooks use to draw NaCl / CsCl / FCC (a corner
 * atom appears at all 8 corners, a face atom on both opposing faces) instead
 * of the lopsided half-open block the compact expansion leaves.
 *
 * Unlike computeGhostAtoms these are NOT bond-driven: a metal completes its
 * corners even though it draws no bonds. They carry isGhost = true (the
 * renderer opts them out of picking/cluster logic and the MD engine never
 * sees them — like every image atom) plus isCosmetic = true, and a
 * ghostSource so they inherit the source atom's KE color and route picks to
 * the real atom.
 *
 * Deduplicated against `existing` positions (real atoms + bond ghosts): the
 * inclusive `expandSupercell` already duplicates frac-0 atoms onto the far
 * faces, so without this guard those crystals would get doubled, z-fighting
 * boundary atoms. With it the function is idempotent regardless of which
 * expansion produced `realAtoms`.
 *
 * `center` is the supercell center the atoms were shifted by (atoms come in
 * origin-centered), needed to map a position back to a supercell-fractional
 * coordinate through periodicBox.hInv.
 */
export const computeCosmeticBoundaryAtoms = (
  realAtoms: Atom[],
  periodicBox: PeriodicBox,
  center: Vector3,
  existing: Atom[],
  startIndex: number,
  // When true, a mirrored atom is kept ONLY if it bonds to at least one REAL
  // atom (covalent frameworks). Without this, a bridging atom whose framework
  // neighbor also lives on the boundary gets mirrored to a spot with no real
  // partner and renders as an isolated floating sphere. Metallic / ionic
  // crystals pass false: their boundary completion is purely positional and a
  // mirrored corner atom is meant to show even with no in-cutoff real neighbor.
  requireRealNeighbor: boolean = false,
): Atom[] => {
  if (realAtoms.length === 0) return [];
  // Precompute bonding radii for the real-neighbor test (non-ionic cutoff —
  // covalent frameworks are the only caller that sets requireRealNeighbor).
  const realRadii = requireRealNeighbor ? realAtoms.map((a) => bondingRadius(a.elementSymbol)) : null;
  const bondsToRealAtom = (pos: Vector3, element: string): boolean => {
    if (!realRadii) return true;
    const r = bondingRadius(element);
    for (let j = 0; j < realAtoms.length; j++) {
      const a = realAtoms[j];
      if (a.removed || a.guestId !== undefined) continue;
      const cutoff = (r + realRadii[j]) * NON_IONIC_SCALE;
      const d2 = pos.distanceToSquared(a.position);
      if (d2 > 0.01 && d2 < cutoff * cutoff) return true;
    }
    return false;
  };
  const h = periodicBox.h.elements; // column-major: [Ax,Ay,Az, Bx,By,Bz, Cx,Cy,Cz]
  const axisVec = [
    new Vector3(h[0], h[1], h[2]), // A (full supercell vector along a)
    new Vector3(h[3], h[4], h[5]), // B
    new Vector3(h[6], h[7], h[8]), // C
  ];
  const FRAC_EPS = 1e-3;
  // Dedup on a 0.01 Å grid so a cosmetic image that coincides with an
  // already-present atom (real, ghost, or a sibling cosmetic copy) is
  // dropped instead of stacking and z-fighting.
  const key = (x: number, y: number, z: number) =>
    `${Math.round(x * 100)},${Math.round(y * 100)},${Math.round(z * 100)}`;
  const seen = new Set<string>();
  for (const a of existing) seen.add(key(a.position.x, a.position.y, a.position.z));
  const cosmetics: Atom[] = [];
  let idx = startIndex;
  const s = new Vector3();
  for (let i = 0; i < realAtoms.length; i++) {
    const a = realAtoms[i];
    if (a.removed || a.guestId !== undefined) continue;
    // Supercell-fractional coordinate. Atoms are origin-centered, so shift
    // back by +center before mapping Cartesian → fractional through hInv.
    s.copy(a.position).add(center).applyMatrix3(periodicBox.hInv);
    const near: number[] = [];
    if (Math.abs(s.x) < FRAC_EPS) near.push(0);
    if (Math.abs(s.y) < FRAC_EPS) near.push(1);
    if (Math.abs(s.z) < FRAC_EPS) near.push(2);
    if (near.length === 0) continue;
    // One mirrored copy per non-empty subset of the lower planes the atom
    // lies on: 1 plane → opposite face (1 copy), 2 → edge (3 copies),
    // 3 → corner (7 copies).
    const subsets = 1 << near.length;
    for (let m = 1; m < subsets; m++) {
      const pos = a.position.clone();
      for (let b = 0; b < near.length; b++) {
        if (m & (1 << b)) pos.add(axisVec[near[b]]);
      }
      const k = key(pos.x, pos.y, pos.z);
      if (seen.has(k)) continue;
      // Drop mirrors that wouldn't attach to the real framework — they'd be
      // isolated floating spheres (covalent frameworks only; see param doc).
      if (!bondsToRealAtom(pos, a.elementSymbol)) continue;
      seen.add(k);
      const ghost = new Atom(idx++, a.elementSymbol, pos);
      ghost.isGhost = true;
      ghost.isCosmetic = true;
      ghost.ghostSource = i;
      ghost.imageOffset = pos.clone().sub(a.position); // constant lattice translation from source
      cosmetics.push(ghost);
    }
  }
  return cosmetics;
};

export const computeBonds = (atoms: Atom[], charges?: Record<string, number>): BondPair[] => {
  const bonds: BondPair[] = [];
  const radii = atoms.map((a) => bondingRadius(a.elementSymbol));
  const isIonic = !!charges;
  const atomCharges = atoms.map((a) => charges?.[a.elementSymbol] ?? 0);
  for (let i = 0; i < atoms.length; i++) {
    for (let j = i + 1; j < atoms.length; j++) {
      let cutoff: number;
      if (isIonic) {
        const c1 = atomCharges[i];
        const c2 = atomCharges[j];
        if (c1 * c2 < 0) {
          // Cation-anion: ionic distance, generous additive tolerance.
          cutoff = radii[i] + radii[j] + IONIC_TOLERANCE;
        } else if (c1 === 0 || c2 === 0) {
          // Mixed: one neutral, fall back to non-ionic scaling.
          cutoff = (radii[i] + radii[j]) * NON_IONIC_SCALE;
        } else {
          // Same-sign ions never bond.
          continue;
        }
      } else {
        cutoff = (radii[i] + radii[j]) * NON_IONIC_SCALE;
      }
      const d = atoms[i].position.distanceTo(atoms[j].position);
      // Lower bound 0.1 Å filters out duplicate atoms (e.g. floating-point
      // drift from boundary expansion) so they don't appear as zero-length
      // self-bonds.
      if (d > 0.1 && d < cutoff) {
        bonds.push({ a: i, b: j });
      }
    }
  }
  return bonds;
};

// Visual profile for each MolecularViewerStyle. Atom geometry comes from
// (atomScale × element radius + probeOffset); bond cylinders, dot-cloud
// rendering, and ESP coloring are flagged per-style. The six surface modes
// don't translate exactly from their molecular-chemistry meaning to inorganic
// crystals, so v1 approximates them as VdW-envelope variations that look
// visually distinct rather than reproducing the molecular-surface algorithms:
//
//   QuickSurface → VdW spheres, high tessellation, fully opaque (smooth blob)
//   ContactSurface → VdW spheres + thick bonds filling contact zones
//   SolventAccessibleSurface → VdW + 1.4 Å probe offset, translucent
//   SolventExcludedSurface → VdW with high tessellation, lightly translucent
//   ElectrostaticPotentialSurface → VdW with ESP-heuristic coloring
//   DotSurface → Fibonacci-sphere point cloud per atom (Points node)
//
// Styles with no crystal analog (Cartoon / Trace / Tube / Sketch2D) fall back
// to BallAndStick at the bottom of the switch — those entries are also hidden
// from the Style dropdown for crystal projects in projectSettingsContent.tsx.
export type RenderMode = 'solid' | 'dots';
export type ColorMode = 'element' | 'esp';

export interface StyleProfile {
  atomScale: number; // multiplied with the element's VdW-ish radius
  probeOffset: number; // absolute Å added after scaling (for SAS)
  atomSegments: number; // sphere tessellation; higher = smoother but slower
  atomOpacity: number; // 1 = opaque; <1 forces transparent
  bondRadius: number;
  showBonds: boolean;
  showLabels: boolean;
  renderMode: RenderMode;
  colorMode: ColorMode;
  dotsPerAtom: number; // used when renderMode === 'dots'
}

const baseSolid = (over: Partial<StyleProfile>): StyleProfile => ({
  atomScale: 0.3,
  probeOffset: 0,
  atomSegments: 16,
  atomOpacity: 1,
  bondRadius: 0.1,
  showBonds: true,
  showLabels: false,
  renderMode: 'solid',
  colorMode: 'element',
  dotsPerAtom: 0,
  ...over,
});

export const styleProfile = (style: MolecularViewerStyle | undefined | null): StyleProfile => {
  switch (style) {
    case MolecularViewerStyle.SpaceFilling:
      return baseSolid({ atomScale: 1.0, bondRadius: 0, showBonds: false });
    case MolecularViewerStyle.Wireframe:
      return baseSolid({ atomScale: 0.08, bondRadius: 0.04 });
    case MolecularViewerStyle.Stick:
      // Atoms are tiny caps at the cylinder ends — the bond IS the visual.
      return baseSolid({ atomScale: 0.16, bondRadius: 0.16 });
    case MolecularViewerStyle.AtomIndex:
      return baseSolid({ showLabels: true });

    // Surface modes — VdW-envelope variations (see header comment).
    case MolecularViewerStyle.QuickSurface:
      return baseSolid({ atomScale: 1.0, bondRadius: 0, showBonds: false, atomSegments: 24 });
    case MolecularViewerStyle.ContactSurface:
      // Atoms at VdW + thick bonds: cylinders fill the gaps so the visual
      // reads as a connected envelope rather than discrete spheres.
      return baseSolid({ atomScale: 1.0, bondRadius: 0.45, showBonds: true, atomSegments: 20 });
    case MolecularViewerStyle.SolventAccessibleSurface:
      // SAS = VdW radius + 1.4 Å water probe; translucent so the underlying
      // atoms are still inferable.
      return baseSolid({
        atomScale: 1.0,
        probeOffset: 1.4,
        bondRadius: 0,
        showBonds: false,
        atomSegments: 20,
        atomOpacity: 0.5,
      });
    case MolecularViewerStyle.SolventExcludedSurface:
      // SES is conceptually VdW envelope minus probe-excluded interior; for
      // an isolated supercell, the practical visual is "smoother, slightly
      // translucent VdW spheres" — distinct enough from SpaceFilling.
      return baseSolid({
        atomScale: 1.0,
        bondRadius: 0,
        showBonds: false,
        atomSegments: 28,
        atomOpacity: 0.85,
      });
    case MolecularViewerStyle.ElectrostaticPotentialSurface:
      return baseSolid({
        atomScale: 1.0,
        bondRadius: 0,
        showBonds: false,
        atomSegments: 24,
        atomOpacity: 0.9,
        colorMode: 'esp',
      });
    case MolecularViewerStyle.DotSurface:
      return baseSolid({
        atomScale: 1.0,
        bondRadius: 0,
        showBonds: false,
        renderMode: 'dots',
        dotsPerAtom: 60,
      });

    case MolecularViewerStyle.BallAndStick:
    default:
      return baseSolid({});
  }
};

// ESP heuristic — a placeholder coloring that buckets common crystal-forming
// elements by typical formal-charge sign. Red for anions (O / S / F / Cl /
// Br / I), blue for cations (alkali / alkaline-earth / common transition
// metals), neutral-gray for elements that are typically near-neutral (C, H,
// Si). Once basis atoms carry a formal-charge field we can replace this with
// a real per-atom mapping (and eventually a Madelung-aware ESP for ionic
// crystals). The point of v1 is to make the ESP style visually distinct
// from element coloring.
const ESP_HEURISTIC: Record<string, string> = {
  // strong anions (negative ESP, red end)
  O: '#ff3333',
  S: '#ff5555',
  F: '#ff6060',
  Cl: '#ff7575',
  Br: '#ff6060',
  I: '#ff5555',
  // alkali / alkaline-earth (positive ESP, blue end)
  Li: '#5080ff',
  Na: '#4060ff',
  K: '#3050ff',
  Rb: '#3050ff',
  Cs: '#2040ff',
  Be: '#5070ff',
  Mg: '#4060ff',
  Ca: '#3050ff',
  Sr: '#3050ff',
  Ba: '#2040ff',
  // common multi-valent cations (moderate positive)
  Ti: '#5070d0',
  V: '#5070c0',
  Cr: '#5060c0',
  Mn: '#6060c0',
  Fe: '#7060a0',
  Co: '#7060a0',
  Ni: '#6080a0',
  Cu: '#a07060',
  Zn: '#8070a0',
  Ag: '#9090a0',
  Au: '#a0a060',
  Sn: '#8090a0',
  Zr: '#5080c0',
  // typical near-neutral
  C: '#dddddd',
  H: '#eeeeee',
  N: '#c0c0e0',
  P: '#d0d0c0',
  Si: '#d0c0a0',
};

export const espColor = (symbol: string): string => ESP_HEURISTIC[symbol] ?? '#aaaaaa';

/**
 * Kinetic-energy "heat map" color: maps a fraction in [0, 1] onto a
 * white → red ramp. The crystal viewer feeds it atomKE / maxKE (the
 * frame's hottest atom), so a motionless atom (fraction 0) is pure
 * white and the fastest atom (fraction 1) is pure red, linearly
 * interpolated — red stays maxed while green and blue fall off.
 */
export const kineticEnergyColor = (fraction: number): string => {
  const f = fraction < 0 ? 0 : fraction > 1 ? 1 : fraction;
  const gb = Math.round(255 * (1 - f));
  return `rgb(255, ${gb}, ${gb})`;
};

// Fibonacci-sphere sampling: evenly distributes `count` points over a sphere
// of radius `radius`. Used for the DotSurface rendering — each crystal atom
// becomes a cloud of `dotsPerAtom` points on its VdW boundary.
export const fibonacciSpherePoints = (count: number, radius: number): [number, number, number][] => {
  if (count <= 0) return [];
  const out: [number, number, number][] = [];
  const phi = Math.PI * (Math.sqrt(5) - 1); // golden angle
  for (let i = 0; i < count; i++) {
    const y = count === 1 ? 0 : 1 - (i / (count - 1)) * 2;
    const r = Math.sqrt(Math.max(0, 1 - y * y));
    const theta = phi * i;
    out.push([Math.cos(theta) * r * radius, y * radius, Math.sin(theta) * r * radius]);
  }
  return out;
};

// Three.js material props for each MolecularViewerMaterial. All map onto a
// single meshStandardMaterial — that handles diffuse / metallic / translucent
// cleanly; toon and backdrop are approximated with flatShading + softer
// roughness rather than dragging in meshToonMaterial.
export interface MaterialProfile {
  roughness: number;
  metalness: number;
  transparent: boolean;
  opacity: number;
  flatShading?: boolean;
}

export const materialProps = (
  material: MolecularViewerMaterial | undefined | null,
  translucentOpacity: number,
): MaterialProfile => {
  switch (material) {
    case MolecularViewerMaterial.Soft:
      return { roughness: 0.8, metalness: 0.0, transparent: false, opacity: 1 };
    case MolecularViewerMaterial.Glossy:
      return { roughness: 0.25, metalness: 0.4, transparent: false, opacity: 1 };
    case MolecularViewerMaterial.Metal:
      return { roughness: 0.3, metalness: 1.0, transparent: false, opacity: 1 };
    case MolecularViewerMaterial.Translucent:
      return { roughness: 0.45, metalness: 0.15, transparent: true, opacity: translucentOpacity };
    case MolecularViewerMaterial.Glass:
      // Glass: low roughness + extra-translucent. Three.js doesn't support
      // real refraction without a physical material + environment map, so
      // this is the best approximation with meshStandardMaterial.
      return { roughness: 0.1, metalness: 0.0, transparent: true, opacity: translucentOpacity * 0.6 };
    case MolecularViewerMaterial.Toon:
      return { roughness: 0.7, metalness: 0.05, transparent: false, opacity: 1, flatShading: true };
    case MolecularViewerMaterial.Flat:
      return { roughness: 0.5, metalness: 0.0, transparent: false, opacity: 1, flatShading: true };
    case MolecularViewerMaterial.Backdrop:
      return { roughness: 0.95, metalness: 0.0, transparent: false, opacity: 1, flatShading: true };
    case MolecularViewerMaterial.Diffuse:
    default:
      return { roughness: 0.45, metalness: 0.15, transparent: false, opacity: 1 };
  }
};
